import React from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'

import ClearableInput, {
  SettingsField,
} from '../../../components/Settings/ClearableInput'
import SettingsCard from '../../../components/Settings/SettingsCard'
import { SOCIAL_FIELDS, socialUrlStatus } from '../../../lib/socialLinks'

/**
 * Social profile links for user or business settings.
 *
 * The ✓/✗ indicator reflects URL *format* only — there is no endpoint that
 * verifies a link resolves or belongs to this entity, so it must not be
 * labelled "verified".
 */
export default function SocialMediaCard({
  data,
  onFieldChange,
  onSave,
  hasChanged,
  isSaving,
  fields = SOCIAL_FIELDS,
  title,
  icon = 'share-nodes',
}) {
  const { t } = useTranslation()

  const anyInvalid = fields.some(
    field => socialUrlStatus(data?.[field.key]) === 'invalid',
  )

  return (
    <SettingsCard
      icon={icon}
      title={title ?? t('users.edit.socials')}
      actions={
        <>
          <button
            className="btn btn-primary"
            type="button"
            onClick={onSave}
            disabled={!hasChanged || isSaving || anyInvalid}
          >
            {isSaving ? t('users.edit.saving') : t('users.edit.save')}
          </button>
          {anyInvalid && (
            <span className="text-danger small">
              {t(
                'users.edit.socials_invalid',
                'Fix the highlighted links before saving.',
              )}
            </span>
          )}
        </>
      }
    >
      <div className="row">
        {fields.map(field => {
          const value = data?.[field.key] ?? ''
          const status = socialUrlStatus(value)
          return (
            <div className="col-md-6" key={field.key}>
              <SettingsField
                label={t(field.labelKey)}
                htmlFor={field.key}
                status={status}
                help={
                  status === 'invalid'
                    ? t(
                        'users.edit.social_invalid_url',
                        'Enter a full URL, e.g. https://example.com/you',
                      )
                    : null
                }
                helpVariant={status === 'invalid' ? 'error' : undefined}
              >
                <ClearableInput
                  id={field.key}
                  name={field.key}
                  type="url"
                  placeholder={field.placeholder}
                  value={value}
                  onChange={newValue => onFieldChange(field.key, newValue)}
                  onClear={() => onFieldChange(field.key, '')}
                  clearLabel={t('settings.field.clear', 'Clear')}
                />
              </SettingsField>
            </div>
          )
        })}
      </div>
    </SettingsCard>
  )
}

SocialMediaCard.propTypes = {
  data: PropTypes.object,
  onFieldChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  hasChanged: PropTypes.bool,
  isSaving: PropTypes.bool,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      labelKey: PropTypes.string.isRequired,
      placeholder: PropTypes.string,
    }),
  ),
  title: PropTypes.node,
  icon: PropTypes.string,
}
